// Invoice-specific building blocks for the full-page invoice workspace.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Banknote, Building2, ChevronDown, ChevronRight, CircleDollarSign, FileText, Landmark,
  Loader2, Lock, Mail, Printer, Send, Wallet, BellRing, Briefcase, CheckCircle2, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SectionCard } from "@/components/QuoteWorkspaceParts";

const nzd = (n) => new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(n || 0);
const inputCls = "h-8 rounded-sm border-zinc-300 dark:border-white/15 dark:bg-white/5 text-sm";
const labelCls = "text-[10px] font-bold uppercase tracking-widest text-zinc-500 dark:text-zinc-400";

export const INVOICE_TYPES = [
  ["standard", "Standard Invoice"], ["deposit", "Deposit Invoice"], ["progress_claim", "Progress Claim"],
  ["final", "Final Invoice"], ["variation", "Variation Invoice"], ["retention", "Retention Claim"],
  ["credit_note", "Credit Note"],
];

export function dueInText(dueDate) {
  if (!dueDate) return null;
  const days = Math.round((new Date(`${dueDate}T00:00:00`) - new Date(new Date().toDateString())) / 86400000);
  if (days > 0) return { text: `Due in ${days} day${days === 1 ? "" : "s"}`, cls: "text-zinc-400" };
  if (days === 0) return { text: "Due today", cls: "text-amber-600 font-bold" };
  return { text: `${-days} day${days === -1 ? "" : "s"} overdue`, cls: "text-red-600 font-bold" };
}

// ==================== INVOICE DETAILS ====================
export function InvoiceDetailsSection({ inv, patch, advancedOpen, setAdvancedOpen, jobs }) {
  const due = dueInText(inv.due_date);
  const adv = inv.advanced || {};
  const setAdv = (k) => (e) => patch({ advanced: { ...adv, [k]: e.target.value } });
  return (
    <SectionCard title="Invoice Details" icon={FileText} testid="invoice-details-section">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <Label className={labelCls}>Invoice Number</Label>
          <div className="relative mt-1.5">
            <Input value={inv.invoice_number || ""} readOnly className={`${inputCls} bg-zinc-50 dark:bg-white/[0.03] font-mono font-bold pr-7`} data-testid="invoice-number" />
            <Lock className="w-3 h-3 text-zinc-400 absolute right-2.5 top-1/2 -translate-y-1/2" title="Auto-numbered" />
          </div>
        </div>
        <div>
          <Label className={labelCls}>Issue Date</Label>
          <Input type="date" value={inv.issue_date || ""} onChange={(e) => patch({ issue_date: e.target.value })} className={`${inputCls} mt-1.5`} data-testid="invoice-issue-date" />
        </div>
        <div>
          <Label className={labelCls}>Due Date</Label>
          <Input type="date" value={inv.due_date || ""} onChange={(e) => patch({ due_date: e.target.value })} className={`${inputCls} mt-1.5`} data-testid="invoice-due-date" />
          {due && <div className={`text-[10px] mt-1 font-mono ${due.cls}`} data-testid="invoice-due-in">{due.text}</div>}
        </div>
        <div>
          <Label className={labelCls}>Reference</Label>
          <Input value={inv.reference || ""} onChange={(e) => patch({ reference: e.target.value })} placeholder="e.g. Stage 1 Progress Claim" className={`${inputCls} mt-1.5`} data-testid="invoice-reference" />
        </div>
      </div>
      <div className="grid sm:grid-cols-[1fr_220px] gap-3 mt-3">
        <div>
          <Label className={labelCls}>Invoice Title</Label>
          <Input value={inv.title || ""} onChange={(e) => patch({ title: e.target.value })} placeholder="e.g. Harbour View Subdivision – Stage 1 Progress Claim" className={`${inputCls} mt-1.5`} data-testid="invoice-title" />
        </div>
        <div className="sm:col-span-2">
          <Label className={labelCls}>Short Description (shown under the title on the customer invoice)</Label>
          <Input value={inv.description || ""} onChange={(e) => patch({ description: e.target.value })} placeholder="e.g. Install 1050dia manhole and 3 × 300dia culvert pipes." className={`${inputCls} mt-1.5`} data-testid="invoice-description" />
        </div>
        <div>
          <Label className={labelCls}>Invoice Type</Label>
          <Select value={inv.invoice_type || "standard"} onValueChange={(v) => patch({ invoice_type: v })}>
            <SelectTrigger className={`${inputCls} mt-1.5`} data-testid="invoice-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{INVOICE_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <button onClick={() => setAdvancedOpen(!advancedOpen)} className="mt-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-[#FF5F15]" data-testid="invoice-advanced-toggle">
        <ChevronRight className={`w-3 h-3 transition-transform ${advancedOpen ? "rotate-90" : ""}`} /> Additional Details
      </button>
      {advancedOpen && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-3 pt-3 border-t border-zinc-100 dark:border-white/5" data-testid="invoice-advanced-fields">
          <div>
            <Label className={labelCls}>Linked Job</Label>
            <Select value={inv.job_id || "none"} onValueChange={(v) => patch({ job_id: v === "none" ? null : v })}>
              <SelectTrigger className={`${inputCls} mt-1.5`} data-testid="invoice-job-select"><SelectValue placeholder="No linked job" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none"><span className="italic text-zinc-500">No linked job</span></SelectItem>
                {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.job_number ? `${j.job_number} – ` : ""}{j.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {[["po_number", "Purchase Order Number"], ["cost_centre", "Cost Centre"], ["internal_ref", "Internal Reference"],
            ["project_manager", "Project Manager"], ["salesperson", "Salesperson"], ["accounting_code", "Accounting Code"],
            ["customer_account_ref", "Customer Account Reference"]].map(([k, label]) => (
            <div key={k}>
              <Label className={labelCls}>{label}</Label>
              <Input value={adv[k] || ""} onChange={setAdv(k)} className={`${inputCls} mt-1.5`} data-testid={`invoice-adv-${k}`} />
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

// ==================== PROGRESS CLAIM ====================
export function ProgressClaimSection({ inv, patch, liveSubtotal }) {
  const pc = inv.progress_claim || {};
  const set = (k) => (e) => patch({ progress_claim: { ...pc, [k]: e.target.value === "" ? null : Number(e.target.value) } });
  const contract = Number(pc.contract_value || 0);
  const variations = Number(pc.approved_variations || 0);
  const revised = contract + variations;
  const prev = Number(pc.previously_claimed || 0);
  const thisClaim = liveSubtotal;
  const totalClaimed = prev + thisClaim;
  const remaining = revised - totalClaimed;
  const over = revised > 0 && remaining < -0.005;
  return (
    <SectionCard title="Progress Claim" icon={CircleDollarSign} testid="invoice-progress-claim">
      <div className="grid grid-cols-3 gap-3">
        {[["contract_value", "Original Contract Value"], ["approved_variations", "Approved Variations"], ["previously_claimed", "Previously Claimed"]].map(([k, label]) => (
          <div key={k}>
            <Label className={labelCls}>{label}</Label>
            <Input type="number" step="0.01" value={pc[k] ?? ""} onChange={set(k)} className={`${inputCls} mt-1.5 text-right`} data-testid={`pc-${k}`} />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-4 text-center">
        {[["Revised Contract", revised], ["This Claim", thisClaim], ["Total Claimed", totalClaimed],
          ["Remaining", remaining], ["Claim %", revised > 0 ? `${((thisClaim / revised) * 100).toFixed(1)}%` : "—"]].map(([label, v]) => (
          <div key={label} className={`border p-2 ${label === "Remaining" && over ? "border-red-400 bg-red-50 dark:bg-red-900/10" : "border-zinc-200 dark:border-white/10"}`}>
            <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-400">{label}</div>
            <div className={`font-mono font-bold text-sm ${label === "This Claim" ? "text-[#FF5F15]" : ""} ${label === "Remaining" && over ? "text-red-600" : ""}`} data-testid={`pc-${label.toLowerCase().replace(/ /g, "-")}`}>
              {typeof v === "number" ? nzd(v) : v}
            </div>
          </div>
        ))}
      </div>
      {over && (
        <div className="mt-3 flex items-center gap-2 text-xs text-red-600 font-bold" data-testid="pc-overclaim-warning">
          <XCircle className="w-4 h-4" /> This claim exceeds the remaining contract value by {nzd(-remaining)} — check before sending.
        </div>
      )}
      <p className="text-[10px] text-zinc-400 mt-2">This claim = the invoice line items subtotal. Retention can be applied in the sidebar.</p>
    </SectionCard>
  );
}

// ==================== INVOICE FROM ====================
export function InvoiceFromSection({ org, inv }) {
  const navigate = useNavigate();
  if (!org) return null;
  return (
    <SectionCard title="Invoice From" icon={Landmark} testid="invoice-from-section"
      right={<button onClick={() => navigate("/app/branding")} className="text-[10px] font-bold uppercase tracking-wider text-[#FF5F15] hover:underline" data-testid="edit-business-details">Edit Business Details</button>}>
      <div className="flex items-start gap-3.5">
        {org.logo_url ? <img src={org.logo_url} alt="logo" className="w-10 h-10 object-contain border border-zinc-200 dark:border-white/10" />
          : <div className="w-10 h-10 bg-[#FF5F15]/10 border border-[#FF5F15]/30 flex items-center justify-center"><Building2 className="w-4.5 h-4.5 text-[#FF5F15]" /></div>}
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1.5 text-sm min-w-0">
          <div className="sm:col-span-2">
            <div className="font-bold">{org.name}</div>
            {org.address && <div className="text-xs text-zinc-500 whitespace-pre-line">{org.address}</div>}
          </div>
          {org.nzbn && <div><span className={labelCls}>NZBN</span><div className="font-mono text-xs mt-0.5">{org.nzbn}</div></div>}
          {inv && inv.gst_enabled !== false && org.gst_number && <div><span className={labelCls}>GST Number</span><div className="font-mono text-xs mt-0.5">{org.gst_number}</div></div>}
          {org.billing_contact_email && <div><span className={labelCls}>Accounts Email</span><div className="text-xs mt-0.5">{org.billing_contact_email}</div></div>}
          {org.billing_contact_phone && <div><span className={labelCls}>Phone</span><div className="text-xs mt-0.5">{org.billing_contact_phone}</div></div>}
        </div>
      </div>
    </SectionCard>
  );
}

// ==================== DELIVERY METHOD ====================
const METHODS = [
  { key: "email", label: "Email", desc: "Send invoice via email.", icon: Mail },
  { key: "manual", label: "Manual", desc: "Download PDF to send manually.", icon: FileText },
  { key: "mail", label: "Mail", desc: "Mark as sent by post.", icon: Printer },
];
export function DeliveryMethodSection({ inv, patch }) {
  return (
    <SectionCard title="Delivery Method" icon={Send} testid="invoice-delivery-section">
      <p className="text-xs text-zinc-500 mb-3">How would you like to send this invoice?</p>
      <div className="grid grid-cols-3 gap-2">
        {METHODS.map((m) => (
          <button key={m.key} onClick={() => patch({ delivery_method: m.key })}
            className={`p-3 border text-left transition-colors ${(inv.delivery_method || "email") === m.key ? "border-[#FF5F15] bg-orange-50/50 dark:bg-[#FF5F15]/10" : "border-zinc-200 dark:border-white/10 hover:border-zinc-400"}`}
            data-testid={`delivery-${m.key}`}>
            <m.icon className={`w-4 h-4 mb-1.5 ${(inv.delivery_method || "email") === m.key ? "text-[#FF5F15]" : "text-zinc-400"}`} />
            <div className="text-xs font-bold uppercase tracking-wider">{m.label}</div>
            <div className="text-[10px] text-zinc-400 mt-0.5">{m.desc}</div>
          </button>
        ))}
      </div>
    </SectionCard>
  );
}

// ==================== PAYMENT TERMS ====================
const TERM_OPTIONS = [["receipt", "Due on Receipt", 0], ["7", "7 Days", 7], ["14", "14 Days", 14], ["20", "20 Days", 20], ["30", "30 Days", 30], ["eom", "End of Month", null], ["custom", "Custom", null]];
export function PaymentTermsSection({ inv, patch, org }) {
  const applyTerm = (key) => {
    const opt = TERM_OPTIONS.find(([k]) => k === key);
    const base = inv.issue_date ? new Date(`${inv.issue_date}T00:00:00`) : new Date();
    let due = inv.due_date;
    if (opt && opt[2] != null) { const d = new Date(base); d.setDate(d.getDate() + opt[2]); due = d.toISOString().slice(0, 10); }
    if (key === "eom") { const d = new Date(base.getFullYear(), base.getMonth() + 1, 0); due = d.toISOString().slice(0, 10); }
    patch({ settings: { ...(inv.settings || {}), terms_key: key }, due_date: due });
  };
  const ed = inv.early_discount || {};
  return (
    <SectionCard title="Payment Terms" icon={Wallet} testid="invoice-payment-terms">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className={labelCls}>Payment Terms</Label>
          <Select value={(inv.settings || {}).terms_key || "14"} onValueChange={applyTerm}>
            <SelectTrigger className={`${inputCls} mt-1.5`} data-testid="invoice-terms-select"><SelectValue /></SelectTrigger>
            <SelectContent>{TERM_OPTIONS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className={labelCls}>Early Discount %</Label>
            <Input type="number" step="0.5" min="0" value={ed.pct ?? ""} onChange={(e) => patch({ early_discount: { ...ed, pct: e.target.value === "" ? null : Number(e.target.value) } })} placeholder="e.g. 2" className={`${inputCls} mt-1.5 text-right`} data-testid="early-discount-pct" />
          </div>
          <div>
            <Label className={labelCls}>If paid within (days)</Label>
            <Input type="number" min="1" value={ed.days ?? ""} onChange={(e) => patch({ early_discount: { ...ed, days: e.target.value === "" ? null : Number(e.target.value) } })} placeholder="e.g. 10" className={`${inputCls} mt-1.5 text-right`} data-testid="early-discount-days" />
          </div>
        </div>
      </div>
      {org?.bank_account && (
        <div className="mt-4 pt-3 border-t border-zinc-100 dark:border-white/5" data-testid="invoice-bank-details">
          <div className={labelCls}>Bank Transfer Details (shown on invoice)</div>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1 mt-1.5 text-xs">
            {org.bank_name && <div><span className="text-zinc-400">Bank:</span> <b>{org.bank_name}</b></div>}
            <div><span className="text-zinc-400">Account Name:</span> <b>{org.bank_account_name || org.name}</b></div>
            <div><span className="text-zinc-400">Account Number:</span> <b className="font-mono">{org.bank_account}</b></div>
            <div><span className="text-zinc-400">Reference:</span> <b className="font-mono text-[#FF5F15]">{inv.invoice_number}</b></div>
          </div>
        </div>
      )}
      <div className="mt-3">
        <Label className={labelCls}>Terms text (printed on invoice)</Label>
        <Textarea rows={2} value={inv.payment_terms || ""} onChange={(e) => patch({ payment_terms: e.target.value })} className="mt-1.5 rounded-sm text-xs dark:bg-white/5" data-testid="invoice-terms-text" />
      </div>
    </SectionCard>
  );
}

// ==================== SUMMARY + PAYMENT STATUS ====================
export function InvoiceSummaryPanel({ totals, inv, patch, onPreview, onSend }) {
  const noGst = inv?.gst_enabled === false;
  return (
    <section className="border-2 border-[#FF5F15]/40 bg-white dark:bg-[#0F141C]" data-testid="invoice-summary-panel">
      <div className="px-4 py-2.5 border-b border-zinc-100 dark:border-white/5 text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500 dark:text-zinc-400 flex items-center gap-2">
        <CircleDollarSign className="w-3.5 h-3.5 text-[#FF5F15]" /> Invoice Summary
      </div>
      {patch && (
        <div className="px-4 pt-3 space-y-2 border-b border-zinc-100 dark:border-white/5 pb-3">
          <label className="flex items-center gap-2 text-xs font-medium cursor-pointer select-none">
            <Checkbox checked={noGst} onCheckedChange={(v) => patch({ gst_enabled: !v })} className="data-[state=checked]:bg-[#FF5F15] data-[state=checked]:border-[#FF5F15]" data-testid="invoice-no-gst" />
            No GST (not GST registered)
          </label>
          {!noGst && (
            <label className="flex items-center gap-2 text-xs font-medium cursor-pointer select-none">
              <Checkbox checked={!!inv?.gst_inclusive} onCheckedChange={(v) => patch({ gst_inclusive: !!v })} className="data-[state=checked]:bg-[#FF5F15] data-[state=checked]:border-[#FF5F15]" data-testid="invoice-gst-inclusive" />
              Prices Include GST
            </label>
          )}
        </div>
      )}
      <div className="p-4 space-y-2 text-sm" data-testid="invoice-totals">
        <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span className="font-mono" data-testid="inv-sum-subtotal">{nzd(totals.subtotal)}</span></div>
        {totals.discount > 0 && <div className="flex justify-between text-emerald-600 dark:text-emerald-400"><span>Discount</span><span className="font-mono" data-testid="inv-sum-discount">-{nzd(totals.discount)}</span></div>}
        {totals.retention > 0 && <div className="flex justify-between text-amber-600"><span>Less Retention ({inv.retention_pct}%)</span><span className="font-mono" data-testid="inv-sum-retention">-{nzd(totals.retention)}</span></div>}
        {inv.gst_enabled !== false && <div className="flex justify-between"><span className="text-zinc-500">Subtotal (excl. GST)</span><span className="font-mono" data-testid="inv-sum-net">{nzd(totals.net)}</span></div>}
        {inv.gst_enabled !== false && <div className="flex justify-between"><span className="text-zinc-500">GST (15%)</span><span className="font-mono" data-testid="inv-sum-gst">{nzd(totals.gst)}</span></div>}
        <div className="border-t-2 border-zinc-200 dark:border-white/10 pt-3 mt-3 flex items-end justify-between">
          <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-500">Total</span>
          <div className="text-right">
            <div className="text-2xl font-black font-mono text-[#FF5F15] leading-none" data-testid="inv-sum-total">{nzd(totals.total)}</div>
            <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-400 mt-1">NZD{inv.gst_enabled === false ? "" : (inv.gst_inclusive ? " · GST inclusive" : " · incl. GST")}</div>
          </div>
        </div>
        <div className="pt-3 space-y-2">
          <Button variant="outline" onClick={onPreview} className="w-full rounded-sm h-9 text-[11px] font-bold uppercase tracking-wider border-zinc-300 dark:border-white/15" data-testid="inv-summary-preview">Preview Invoice PDF</Button>
          <Button onClick={onSend} className="w-full rounded-sm h-10 bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider" data-testid="inv-summary-send">
            <Send className="w-3.5 h-3.5 mr-2" /> Send Invoice to Customer
          </Button>
        </div>
      </div>
    </section>
  );
}

export function PaymentStatusPanel({ inv, onRecord }) {
  const total = inv.total || 0;
  const paid = inv.amount_paid || 0;
  const credits = inv.credits_applied || 0;
  const balance = inv.balance_due != null ? inv.balance_due : total - paid - credits;
  const pct = total > 0 ? Math.min(100, Math.round(((paid + credits) / total) * 100)) : 0;
  const badge = balance <= 0.005 && total > 0 ? ["PAID", "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"]
    : paid + credits > 0 ? ["PARTIALLY PAID", "bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300"]
    : ["UNPAID", "bg-zinc-200 text-zinc-600 dark:bg-white/10 dark:text-zinc-300"];
  return (
    <section className="border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0F141C] p-4" data-testid="invoice-payment-status">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500 dark:text-zinc-400 flex items-center gap-2"><Banknote className="w-3.5 h-3.5 text-[#FF5F15]" /> Payment Status</span>
        <span className={`px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${badge[1]}`} data-testid="payment-status-badge">{badge[0]}</span>
      </div>
      <div className="space-y-1.5 text-xs">
        <div className="flex justify-between"><span className="text-zinc-500">Invoice Total</span><span className="font-mono">{nzd(total)}</span></div>
        <div className="flex justify-between"><span className="text-zinc-500">Amount Paid</span><span className="font-mono" data-testid="ps-amount-paid">{nzd(paid)}</span></div>
        <div className="flex justify-between"><span className="text-zinc-500">Credits Applied</span><span className="font-mono" data-testid="ps-credits">{nzd(credits)}</span></div>
        <div className="flex justify-between font-bold"><span>Balance Due</span><span className="font-mono text-[#FF5F15]" data-testid="ps-balance">{nzd(balance)}</span></div>
      </div>
      <div className="mt-3">
        <div className="h-1.5 bg-zinc-100 dark:bg-white/10 overflow-hidden"><div className="h-full bg-[#FF5F15]" style={{ width: `${pct}%` }} /></div>
        <div className="text-[10px] text-zinc-400 mt-1 font-mono">{pct}% paid</div>
      </div>
      {(inv.payments || []).length > 0 && (
        <div className="mt-3 space-y-1">
          {inv.payments.map((p) => (
            <div key={p.id} className="flex justify-between text-[10px] font-mono text-zinc-500" data-testid={`payment-row-${p.id}`}>
              <span>{p.date} · {p.method}</span><span>{nzd(p.amount)}</span>
            </div>
          ))}
        </div>
      )}
      <Button onClick={onRecord} className="mt-3 w-full rounded-sm h-8 bg-zinc-900 hover:bg-zinc-800 dark:bg-white/10 dark:hover:bg-white/20 text-white text-[10px] font-bold uppercase tracking-wider" data-testid="record-payment-btn">
        $ Record Payment
      </Button>
    </section>
  );
}

export function RecordPaymentDialog({ open, onClose, inv, onSaved }) {
  const balance = inv?.balance_due != null ? inv.balance_due : (inv?.total || 0);
  const [form, setForm] = useState({ amount: "", date: new Date().toISOString().slice(0, 10), method: "bank_transfer", reference: "", notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm((f) => ({ ...f, amount: balance > 0 ? balance.toFixed(2) : "" })); }, [open]); // eslint-disable-line
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/customer-invoices/${inv.id}/payments`, { ...form, amount: Number(form.amount) });
      onSaved(data);
      toast.success(`Payment of ${nzd(Number(form.amount))} recorded`);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Payment failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-md" data-testid="record-payment-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase tracking-tight">Record Payment</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className={labelCls}>Amount (balance {nzd(balance)})</Label>
              <Input required type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={`${inputCls} mt-1.5 text-right`} data-testid="payment-amount" />
            </div>
            <div>
              <Label className={labelCls}>Payment Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={`${inputCls} mt-1.5`} data-testid="payment-date" />
            </div>
          </div>
          <div>
            <Label className={labelCls}>Method</Label>
            <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
              <SelectTrigger className={`${inputCls} mt-1.5`} data-testid="payment-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[["bank_transfer", "Bank Transfer"], ["credit_card", "Credit Card"], ["cash", "Cash"], ["direct_debit", "Direct Debit"], ["other", "Other"]].map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className={labelCls}>Reference</Label>
            <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder={inv?.invoice_number} className={`${inputCls} mt-1.5`} data-testid="payment-reference" />
          </div>
          <div>
            <Label className={labelCls}>Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inputCls} mt-1.5`} />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose} className="rounded-sm text-[11px] font-bold uppercase">Cancel</Button>
            <Button type="submit" disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider" data-testid="payment-save">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />} Record Payment
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ==================== REMINDERS ====================
const REMINDER_ROWS = [
  ["before_due", "Before due", "3 days before due date"],
  ["first", "1st reminder", "7 days after due date"],
  ["second", "2nd reminder", "14 days after due date"],
  ["final", "Final reminder", "30 days after due date"],
];
export function RemindersPanel({ inv, patch }) {
  const r = inv.reminders || {};
  return (
    <section className="border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0F141C] p-4" data-testid="invoice-reminders-panel">
      <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500 dark:text-zinc-400 mb-3 flex items-center gap-2"><BellRing className="w-3.5 h-3.5 text-[#FF5F15]" /> Reminders</div>
      <div className="space-y-2.5">
        {REMINDER_ROWS.map(([k, label, desc]) => (
          <label key={k} className="flex items-center justify-between cursor-pointer select-none">
            <div>
              <div className="text-xs font-bold">{label}</div>
              <div className="text-[10px] text-zinc-400">{desc}</div>
            </div>
            <Checkbox checked={!!r[k]} onCheckedChange={(v) => patch({ reminders: { ...r, [k]: !!v } })} className="data-[state=checked]:bg-[#FF5F15] data-[state=checked]:border-[#FF5F15]" data-testid={`reminder-${k}`} />
          </label>
        ))}
      </div>
      <p className="text-[10px] text-zinc-400 mt-2.5">Reminders stop automatically once the invoice is fully paid.</p>
    </section>
  );
}

// ==================== JOB BILLING OVERVIEW ====================
export function JobBillingOverview({ inv, liveTotal }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open || !inv.job_id || data) return;
    Promise.all([
      api.get(`/jobs/${inv.job_id}`),
      api.get("/customer-invoices", { params: { job_id: inv.job_id } }),
    ]).then(([j, invs]) => {
      const others = (invs.data || []).filter((x) => x.id !== inv.id && x.status !== "cancelled" && x.status !== "void" && x.invoice_type !== "credit_note");
      const prev = others.reduce((s, x) => s + (x.total || 0), 0);
      const paid = others.reduce((s, x) => s + (x.amount_paid || x.paid_amount || 0), 0);
      setData({ job: j.data, prev, paid });
    }).catch(() => setData({ job: null, prev: 0, paid: 0 }));
  }, [open, inv.job_id]); // eslint-disable-line
  if (!inv.job_id) return null;
  const contract = data?.job?.quoted_value || data?.job?.contract_value || 0;
  return (
    <section className="border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0F141C]" data-testid="job-billing-overview">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-2.5" data-testid="jbo-toggle">
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-500 dark:text-zinc-400 flex items-center gap-2"><Briefcase className="w-3.5 h-3.5 text-[#FF5F15]" /> Job Billing Overview</span>
        <ChevronDown className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-1.5 text-xs border-t border-zinc-100 dark:border-white/5 pt-3">
          {!data ? <Loader2 className="w-4 h-4 animate-spin text-[#FF5F15]" /> : (
            <>
              {contract > 0 && <div className="flex justify-between"><span className="text-zinc-500">Contract Value</span><span className="font-mono">{nzd(contract)}</span></div>}
              <div className="flex justify-between"><span className="text-zinc-500">Previously Invoiced</span><span className="font-mono" data-testid="jbo-prev">{nzd(data.prev)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Current Invoice</span><span className="font-mono text-[#FF5F15]">{nzd(liveTotal)}</span></div>
              <div className="flex justify-between font-bold"><span>Total Invoiced</span><span className="font-mono">{nzd(data.prev + liveTotal)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Payments Received</span><span className="font-mono">{nzd(data.paid)}</span></div>
              {contract > 0 && <div className="flex justify-between"><span className="text-zinc-500">Remaining to Invoice</span><span className={`font-mono ${contract - data.prev - liveTotal < 0 ? "text-red-600 font-bold" : ""}`}>{nzd(contract - data.prev - liveTotal)}</span></div>}
            </>
          )}
        </div>
      )}
    </section>
  );
}

// ==================== VARIATIONS IMPORT ====================
export function ImportVariationsDialog({ open, onClose, jobId, onAdd }) {
  const [vars, setVars] = useState([]);
  const [picked, setPicked] = useState({});
  useEffect(() => {
    if (!open || !jobId) return;
    setPicked({});
    api.get(`/jobs/${jobId}/variations`).then((r) => setVars((r.data || []).filter((v) => ["approved", "invoiced"].includes(v.status)))).catch(() => setVars([]));
  }, [open, jobId]);
  const add = () => {
    const chosen = vars.filter((v) => picked[v.id]).map((v) => ({
      description: `Variation ${v.ref || v.number || ""} — ${v.title || v.description || "Approved variation"}`.trim(),
      qty: 1, unit: "ea", unit_price: v.amount || v.value || 0, discount_pct: 0, is_section: false,
      source_variation_id: v.id, detail: "", internal_note: "",
    }));
    if (chosen.length) onAdd(chosen);
    onClose();
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="import-variations-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase tracking-tight">Approved Variations</DialogTitle></DialogHeader>
        {!jobId && <p className="text-sm text-zinc-500">Link a job first (Invoice Details → Additional Details).</p>}
        <div className="max-h-72 overflow-y-auto divide-y divide-zinc-100 dark:divide-white/5 border border-zinc-200 dark:border-white/10">
          {vars.length === 0 && <div className="p-6 text-center text-sm text-zinc-400">No approved variations on this job.</div>}
          {vars.map((v) => (
            <label key={v.id} className={`flex items-center gap-3 px-3 py-2 ${v.status === "invoiced" ? "opacity-50" : "hover:bg-zinc-50 dark:hover:bg-white/5 cursor-pointer"}`} data-testid={`variation-row-${v.id}`}>
              <Checkbox disabled={v.status === "invoiced"} checked={!!picked[v.id]} onCheckedChange={(x) => setPicked((p) => ({ ...p, [v.id]: !!x }))} className="data-[state=checked]:bg-[#FF5F15] data-[state=checked]:border-[#FF5F15]" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{v.ref || v.number || "VAR"} — {v.title || v.description}</div>
                <span className={`text-[9px] font-bold uppercase tracking-wider ${v.status === "invoiced" ? "text-violet-500" : "text-emerald-600"}`}>{v.status}</span>
              </div>
              <span className="font-mono text-xs font-bold">{nzd(v.amount || v.value)}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end">
          <Button onClick={add} disabled={!Object.values(picked).some(Boolean)} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider" data-testid="variations-add">Add Selected</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ==================== SEND DIALOG w/ invoice check ====================
export function SendInvoiceDialog({ open, onClose, inv, totals, saveNow, onSent }) {
  const [email, setEmail] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      setEmail(inv.client_email || "");
      setSubject(`Invoice ${inv.invoice_number}${inv.title ? ` – ${inv.title}` : inv.reference ? ` – ${inv.reference}` : ""}`);
    }
  }, [open]); // eslint-disable-line
  const checks = [
    ["Customer selected", !!(inv.client_name || "").trim()],
    ["Billing email", /.+@.+\..+/.test(email)],
    ["Invoice number", !!inv.invoice_number],
    ["Due date", !!inv.due_date],
    ["Line items", (inv.line_items || []).some((li) => !li.is_section && (li.description || "").trim())],
    ["Amounts valid", (inv.line_items || []).every((li) => li.is_section || ((li.qty || 0) >= 0 && (li.unit_price || 0) >= 0)) && totals.total > 0],
  ];
  const ready = checks.every(([, ok]) => ok);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await saveNow({ client_email: email });
      const { data } = await api.post(`/customer-invoices/${inv.id}/send`, { to_email: email, cc_email: cc || undefined, subject, message: message || undefined });
      onSent({ status: "sent", sent_at: new Date().toISOString(), ...(data || {}) });
      toast.success(`Invoice sent to ${email}`);
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm max-w-md" data-testid="invoice-send-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase tracking-tight">Send {inv?.invoice_number}</DialogTitle></DialogHeader>
        <div className="border border-zinc-200 dark:border-white/10 p-3 space-y-1" data-testid="invoice-check">
          {checks.map(([label, ok]) => (
            <div key={label} className="flex items-center gap-2 text-xs">
              {ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <XCircle className="w-3.5 h-3.5 text-red-500" />}
              <span className={ok ? "" : "text-red-600 font-bold"}>{label}</span>
            </div>
          ))}
          <div className={`text-[10px] font-bold uppercase tracking-widest pt-1 ${ready ? "text-emerald-600" : "text-red-500"}`} data-testid="invoice-check-status">
            {ready ? "✓ Ready to send" : "Fix the items above before sending"}
          </div>
        </div>
        <form onSubmit={send} className="space-y-2.5">
          <div>
            <Label className={labelCls}>To</Label>
            <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} mt-1`} data-testid="inv-send-to" />
          </div>
          <div>
            <Label className={labelCls}>CC (optional)</Label>
            <Input type="email" value={cc} onChange={(e) => setCc(e.target.value)} className={`${inputCls} mt-1`} data-testid="inv-send-cc" />
          </div>
          <div>
            <Label className={labelCls}>Subject</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className={`${inputCls} mt-1`} data-testid="inv-send-subject" />
          </div>
          <div>
            <Label className={labelCls}>Message</Label>
            <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="A short note to go with the invoice…" className="mt-1 rounded-sm text-sm" data-testid="inv-send-message" />
          </div>
          <p className="text-[10px] text-zinc-400">The invoice PDF is attached automatically.</p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose} className="rounded-sm text-[11px] font-bold uppercase">Cancel</Button>
            <Button type="submit" disabled={busy || !ready} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white text-[11px] font-bold uppercase tracking-wider" data-testid="inv-send-confirm">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Send className="w-3.5 h-3.5 mr-1.5" />} Send Invoice
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
